import { useState, useEffect, useCallback, useMemo } from 'react';
import { assetsClipPartialUpdate } from '@goldcast/api/content';
import { formSpeakerDetails, getSpeakersPayloadParams } from '@goldcast/player-config/src/utils/speakerUtils';
import { fetchSpeakers } from '@goldcast/player-config/src/retrievers/fetchSpeakers';
import { fetchTranscript } from '@goldcast/player-config/src/retrievers/fetchTranscript';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { Word } from '@goldcast/player-config/src/types/transcript-types';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { isClipFromUploads } from '@goldcast/player-config/src/utils/clipUtils';
import { UseCaptionGenerationProps, UseCaptionGenerationReturn } from '../types';
import { computeCaptionsWithData } from '@/App/remotion/libs/caption/caption';
import { fullTranscriptStore } from '@/stores/fullTranscriptStore';
import { showErrorToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';

export function useCaptionGeneration({
  clip,
  onCaptionsGenerated
}: UseCaptionGenerationProps): UseCaptionGenerationReturn {
  const { logger } = useAppContext();
  const [generatedCaptions, setGeneratedCaptions] = useState<Word[]>([]);
  const [speakers, setSpeakers] = useState<Record<string, SpeakerWithDetails>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingSpeakers, setIsLoadingSpeakers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSpeakersData = useCallback(
    async (eventId: string, broadcastId: string) => {
      const isFromUploads = isClipFromUploads(clip);

      const [sessionSpeakers] = await Promise.allSettled([
        fetchSpeakers({
          queryParams: getSpeakersPayloadParams(broadcastId, !isFromUploads),
          onErrorToast: showErrorToast
        })
      ]);

      const speakersData = sessionSpeakers.status === 'fulfilled' ? sessionSpeakers.value : {};

      return formSpeakerDetails(speakersData);
    },
    [clip]
  );

  const persistCaptions = useCallback(
    async (captionsWithIndex: Word[]) => {
      try {
        await assetsClipPartialUpdate({
          id: clip.id,
          body: {
            asset_metadata: {
              ...clip.asset_metadata,
              captions: captionsWithIndex
            }
          }
        });

        logger.info('Captions persisted successfully for clip:', clip.id);

        const updatedClip: Clip = {
          ...clip,
          asset_metadata: {
            ...clip.asset_metadata,
            captions: captionsWithIndex
          }
        };

        onCaptionsGenerated?.(updatedClip);
      } catch (err) {
        logger.error('Failed to persist captions:', err);
      }
    },
    [clip, logger, onCaptionsGenerated]
  );

  const generateCaptions = useCallback(
    async (eventId: string, broadcastId: string) => {
      setIsGenerating(true);
      setError(null);

      try {
        const cacheSuffix = Math.random().toString();

        const [transcriptResult, formattedSpeakers] = await Promise.all([
          fetchTranscript({ cacheSuffix, event: eventId, broadcast: broadcastId, onError: showErrorToast }),
          loadSpeakersData(eventId, broadcastId)
        ]);

        if (!transcriptResult.fullTranscript || transcriptResult.fullTranscript.length === 0) {
          setError('No transcript data available');
          return;
        }

        fullTranscriptStore.update(() => transcriptResult.fullTranscript);
        setSpeakers(formattedSpeakers);

        const result = computeCaptionsWithData({
          start: clip.asset_metadata.start,
          end: clip.asset_metadata.end,
          edits: clip.asset_metadata.edits || {},
          deletes: clip.asset_metadata.deletes || {},
          merges: clip.asset_metadata.merges || [],
          isTranslated: false,
          translatedCaptionsSource: []
        });

        if (result.captions && result.captions.length > 0) {
          const captionsWithIndex = result.captions.map((caption: any, index: number) => ({
            ...caption,
            index
          })) as Word[];

          setGeneratedCaptions(captionsWithIndex);
          await persistCaptions(captionsWithIndex);
        } else {
          setError('Unable to generate captions from transcript');
        }
      } catch (err) {
        logger.error('Error generating captions:', err);
        setError('Failed to generate captions');
      } finally {
        setIsGenerating(false);
      }
    },
    [clip, logger, persistCaptions, loadSpeakersData]
  );

  const eventId = clip?.content?.project?.id || clip?.event;
  const broadcastId = clip?.content?.id || clip?.broadcast;
  const hasExistingCaptions = !!(clip?.asset_metadata?.captions && clip.asset_metadata.captions.length > 0);
  const clipStart = clip.asset_metadata?.start;
  const clipEnd = clip.asset_metadata?.end;

  useEffect(() => {
    if (!eventId || !broadcastId) {
      return;
    }

    const hasSpeakers = Object.keys(speakers).length > 0;

    if (hasExistingCaptions && !hasSpeakers && !isLoadingSpeakers) {
      setIsLoadingSpeakers(true);

      loadSpeakersData(eventId, broadcastId)
        .then(formattedSpeakers => {
          setSpeakers(formattedSpeakers);
        })
        .catch(err => {
          logger.error('Error loading speakers:', err);
        })
        .finally(() => {
          setIsLoadingSpeakers(false);
        });
      return;
    }

    if (!hasExistingCaptions && !isGenerating && generatedCaptions.length === 0) {
      const shouldGenerateCaptions = clipStart !== undefined && clipEnd !== undefined;

      if (shouldGenerateCaptions) {
        generateCaptions(eventId, broadcastId);
      }
    }
  }, [
    eventId,
    broadcastId,
    hasExistingCaptions,
    clipStart,
    clipEnd,
    isGenerating,
    generatedCaptions.length,
    speakers,
    isLoadingSpeakers,
    generateCaptions,
    loadSpeakersData,
    logger
  ]);

  const captions = useMemo(
    () => clip?.asset_metadata?.captions || generatedCaptions,
    [clip?.asset_metadata?.captions, generatedCaptions]
  );

  return {
    captions,
    speakers,
    isGenerating,
    isLoadingSpeakers,
    error
  };
}
